import { ModalAction } from '../ModalAction'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { assertWriteIdentity, captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { insertRow, patchRow } from '@/core/lib/optimistic'
import type { DashboardCoaching } from '@/core/types'
import { erstgespraechForm } from '../../../templates'
import { updateFormField, initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { setAt, getAt } from '@/core/lib/path'
import { todayIso } from '@/core/lib/dates'
import { gateAdvance } from '@/core/lib/advanceGate'
import { useAutosave } from '@/core/hooks/useAutosave'
import { me } from '@/core/data/session'
import {
  loadErstgespraech,
  saveErstgespraechDraft,
  checkErstgespraech,
  erstgespraechManualFields,
  advanceNewErstgespraech,
  advanceExistingErstgespraech,
  adaptLegacyVoraussetzungen,
  erstgespraechCardName,
  newErstgespraechCard,
} from '../../../flows/erstgespraech'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { ScrollBar, toastError, toastSuccess } from 'admination-react'

export function ErstgespraechCheckliste({ coachingId }: { coachingId: string | null }) {
  const { state, dispatch, reloadDashboard, optimisticDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [values, setValues] = useState<FormValues | null>(null)
  const [attempted, setAttempted] = useState(false)
  const ready = values !== null

  const setField = useCallback((path: string, v: unknown) => {
    setValues((prev) => updateFormField(prev, path, v))
  }, [])

  const check = useMemo(() => (values ? checkErstgespraech(values) : null), [values])

  const pendingRef = useRef(state.pendingErstgespraech)
  useEffect(() => {
    let cancelled = false
    async function load() {
      const restored = pendingRef.current
      const saved =
        restored && (coachingId === null || restored.coachingId === coachingId)
          ? restored.values
          : coachingId
            ? await loadErstgespraech(coachingId)
            : undefined
      const base = adaptLegacyVoraussetzungen(saved) ?? {
        Maßnahme: { 'Datum des Gesprächs': todayIso() },
      }
      const init = initialFormValues(erstgespraechForm, base)
      const existing = String(getAt(init, 'Gesprächsführer.Name, Vorname') ?? '').trim()
      if (!existing) {
        try {
          const { vorname, nachname } = await me()
          const name = nachname && vorname ? `${nachname}, ${vorname}` : nachname || vorname
          if (name) setAt(init as Record<string, unknown>, 'Gesprächsführer.Name, Vorname', name)
        } catch (error) { void error }
      }
      if (!cancelled) {
        setValues(init)
        setAttempted(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const save = useCallback(
    async (v: FormValues) => {
      if (!coachingId) return
      await saveErstgespraechDraft(coachingId, v)
    },
    [coachingId],
  )
  const { flush } = useAutosave(values, ready && !!coachingId, (v) => (v ? save(v) : Promise.resolve()), 250, { form: "ErstgespraechCheckliste", record: coachingId ?? undefined, label: "Erstgespräch", recover: setValues })

  function advance() {
    if (!values) return
    const identity = captureWriteIdentity()
    const source = state.screen?.source ?? null
    if (coachingId) {
      const name = erstgespraechCardName(values)
      const card = optimisticDashboard(
        name ? patchRow<DashboardCoaching>(coachingId, { name }) : (rows) => rows,
      )
      const retry = { values, coachingId, teilnehmendeId: '' }
      const pending = flush()
      dispatch({ type: 'CLOSE' })
      Promise.resolve(pending)
        .then(() => { assertWriteIdentity(identity); return advanceExistingErstgespraech(coachingId, values) })
        .then((link) => {
          if (!isWriteIdentityCurrent(identity)) return
          card.confirm()
          toastSuccess(`Stammdaten-Link an ${link.sent_to} gesendet.`)
        })
        .catch((e) => {
          if (!isWriteIdentityCurrent(identity)) return
          card.rollback()
          reloadDashboard()
          console.error("Erstgespräch konnte nicht gespeichert werden:", e)
          toastError(
            friendlyErrorMessage(e, "Erstgespräch konnte nicht gespeichert werden. Bitte klicken Sie erneut auf „Weiter“."),
          )
          dispatch({ type: 'REOPEN_ERSTGESPRAECH', coachingId, pending: retry, source })
        })
    } else {
      const prev = state.pendingErstgespraech
      const pending = {
        values,
        coachingId: prev?.coachingId ?? crypto.randomUUID(),
        teilnehmendeId: prev?.teilnehmendeId ?? crypto.randomUUID(),
      }
      const card = optimisticDashboard(insertRow(newErstgespraechCard(pending)))
      dispatch({ type: 'CLOSE' })
      advanceNewErstgespraech(pending)
        .then((link) => {
          if (!isWriteIdentityCurrent(identity)) return
          card.confirm()
          toastSuccess(`Stammdaten-Link an ${link.sent_to} gesendet.`)
        })
        .catch((e) => {
          if (!isWriteIdentityCurrent(identity)) return
          card.rollback()
          reloadDashboard()
          console.error("Teilnehmer/Link konnte nicht erstellt werden:", e)
          toastError(
            friendlyErrorMessage(e, "Teilnehmer/Link konnte nicht erstellt werden. Bitte klicken Sie erneut auf „Weiter“."),
          )
          dispatch({ type: 'REOPEN_ERSTGESPRAECH', coachingId: null, pending, source })
        })
    }
  }

  return (
    <>
      <ModalHeader>
        <div className="pill-cluster">
          <ModalAction
            icon="i-send"
            primaryAction
            title="Link senden"
            onClick={() => {
              setAttempted(true)
              gateAdvance(check, advance)
            }}
          />
        </div>
      </ModalHeader>
      <main className="form" data-scroll-host ref={scrollerRef}>
        {values && (
          <FormRenderer
              definition={erstgespraechForm}
              values={values}
              onChange={setField}
              errors={check?.errors}
              attemptedAdvance={attempted}
              field={(field) => ({
                required: erstgespraechManualFields?.has(field.id),
                missing: erstgespraechManualFields?.has(field.id) ? 'mark' : undefined,
              })}
            />
        )}
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
